"use client"

import { useMemo, useState } from "react"
import { usePathname } from "next/navigation"
import { Header } from "@/components/header"
import { PromptCard } from "@/components/prompt-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, Filter } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { PromptSummary } from "@/lib/mock-data"
import { useAppState } from "@/lib/app-state"
import { dictionary, getLocaleFromPathname } from "@/lib/i18n"

type Props = {
  initialPrompts: PromptSummary[]
  listError?: string
}

export function PromptsPageClient({ initialPrompts, listError }: Props) {
  const pathname = usePathname()
  const locale = getLocaleFromPathname(pathname)
  const t = dictionary[locale].prompts
  const { favorites, addToCart, toggleFavorite } = useAppState()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [sortBy, setSortBy] = useState("popular")

  const filteredPrompts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const filtered = initialPrompts.filter((prompt) => {
      const matchesSearch =
        normalizedQuery.length === 0 ||
        prompt.title.toLowerCase().includes(normalizedQuery) ||
        prompt.description.toLowerCase().includes(normalizedQuery) ||
        prompt.author.toLowerCase().includes(normalizedQuery) ||
        prompt.tags?.some((tag) => tag.toLowerCase().includes(normalizedQuery))
      const matchesCategory = selectedCategory === "all" || prompt.category === selectedCategory
      return matchesSearch && matchesCategory
    })

    const sorted = [...filtered]
    if (sortBy === "popular") {
      sorted.sort((a, b) => b.reviewCount - a.reviewCount)
    } else if (sortBy === "newest") {
      sorted.sort((a, b) => {
        const tb = b.createdAt ? Date.parse(b.createdAt) : Number(b.id)
        const ta = a.createdAt ? Date.parse(a.createdAt) : Number(a.id)
        return tb - ta
      })
    } else if (sortBy === "price") {
      sorted.sort((a, b) => a.price - b.price)
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => b.rating - a.rating)
    }
    return sorted
  }, [initialPrompts, searchQuery, selectedCategory, sortBy])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {listError ? (
        <div className="border-b border-destructive/50 bg-destructive/10 px-4 py-3 text-center text-sm text-destructive">
          프롬프트 목록을 불러오지 못했습니다: {listError}
        </div>
      ) : null}

      {/* Page Header */}
      <section className="border-b py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">{t.title}</h1>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="border-b py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder={t.all} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all}</SelectItem>
                <SelectItem value="마케팅">{locale === "en" ? "Marketing" : "마케팅"}</SelectItem>
                <SelectItem value="광고">{locale === "en" ? "Ads" : "광고"}</SelectItem>
                <SelectItem value="이미지 생성">{locale === "en" ? "Image Generation" : "이미지 생성"}</SelectItem>
                <SelectItem value="창작">{locale === "en" ? "Creative" : "창작"}</SelectItem>
                <SelectItem value="온라인">{locale === "en" ? "Online" : "온라인"}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder={t.popular} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">{t.popular}</SelectItem>
                <SelectItem value="newest">{t.newest}</SelectItem>
                <SelectItem value="price">{t.price}</SelectItem>
                <SelectItem value="rating">{t.rating}</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" className="bg-transparent">
              <Filter className="h-4 w-4 mr-2" />
              {t.filter}
            </Button>
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <p className="text-muted-foreground">
              {t.totalResults.replace("{count}", String(filteredPrompts.length))}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredPrompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                {...prompt}
                isFavorited={favorites.includes(prompt.id)}
                onAddToCart={addToCart}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>

          {filteredPrompts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-muted-foreground">{t.noResults}</p>
              <p className="mt-2 text-muted-foreground/80">{t.tryAnotherKeyword}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
